import { createFileRoute } from '@tanstack/react-router'
import { ClipboardCheck, Check, X } from 'lucide-react'
import { useState } from 'react'

import { BatchApprovalToolbar } from '@/components/common/batch-approval-toolbar'
import { useToast } from '@/components/ui/toast'
import { useConfirmDialog } from '@/hooks'
import {
  usePendingLeaves,
  useApproveLeave,
  useRejectLeave,
  useBatchApproveLeaves,
  useBatchRejectLeaves,
  type LeaveType,
  type Leave,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/hr/leaves/pending')({
  component: PendingLeavesPage,
})

const TYPE_LABELS: Record<LeaveType, string> = {
  annual: '年假',
  sick: '病假',
  personal: '事假',
  marriage: '婚假',
  maternity: '产假',
  paternity: '陪产假',
  bereavement: '丧假',
}

function PendingLeavesPage() {
  const { data: leaves, isLoading, error } = usePendingLeaves()
  const approveMutation = useApproveLeave()
  const rejectMutation = useRejectLeave()
  const batchApproveMutation = useBatchApproveLeaves()
  const batchRejectMutation = useBatchRejectLeaves()
  const toast = useToast()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const handleApprove = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'approve',
      '确定要批准该请假申请吗？'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await approveMutation.mutateAsync(id)
        toast.success('审批成功')
      } catch (error) {
        toast.error((error as Error).message || '审批失败')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleReject = (id: string) => {
    if (!rejectReason.trim()) {
      toast.error('请输入拒绝原因')
      return
    }
    rejectMutation.mutate(
      { id, reason: rejectReason },
      {
        onSuccess: () => {
          setRejectingId(null)
          setRejectReason('')
          toast.success('已拒绝')
        },
        onError: (error) => {
          toast.error(error.message || '操作失败')
        },
      }
    )
  }

  const handleBatchApprove = async (ids: string[]) => {
    return new Promise<{ success: string[]; failed: Array<{ id: string; reason: string }> }>(
      (resolve) => {
        batchApproveMutation.mutate(ids, {
          onSuccess: (result) => {
            if (result.success.length > 0) {
              toast.success(`成功批准 ${result.success.length} 项`)
            }
            resolve(result)
          },
          onError: (error) => {
            toast.error(error.message || '批量审批失败')
            resolve({ success: [], failed: ids.map((id) => ({ id, reason: '操作失败' })) })
          },
        })
      }
    )
  }

  const handleBatchReject = async (ids: string[], reason: string) => {
    return new Promise<{ success: string[]; failed: Array<{ id: string; reason: string }> }>(
      (resolve) => {
        batchRejectMutation.mutate(
          { ids, reason },
          {
            onSuccess: (result) => {
              if (result.success.length > 0) {
                toast.success(`成功拒绝 ${result.success.length} 项`)
              }
              resolve(result)
            },
            onError: (error) => {
              toast.error(error.message || '批量拒绝失败')
              resolve({ success: [], failed: ids.map((id) => ({ id, reason: '操作失败' })) })
            },
          }
        )
      }
    )
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (!leaves) {return}
    if (selectedIds.length === leaves.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(leaves.map((l) => l.id))
    }
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">待审批</h1>
        <p className="text-muted-foreground">审批下属的请假申请</p>
      </div>

      {/* Batch Approval Toolbar */}
      <BatchApprovalToolbar
        selectedIds={selectedIds}
        entityType="leave"
        items={leaves?.map(l => ({ id: l.id, type: l.type })) ?? []}
        onApprove={handleBatchApprove}
        onReject={handleBatchReject}
        onClear={() => setSelectedIds([])}
      />

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : !leaves || leaves.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <ClipboardCheck className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无待审批申请</p>
            <p className="text-sm text-muted-foreground">所有请假申请都已处理</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select All */}
          {leaves.length > 1 && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="select-all"
                checked={selectedIds.length === leaves.length}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="select-all" className="text-sm text-muted-foreground">
                全选 ({leaves.length} 项)
              </label>
            </div>
          )}

          {leaves.map((leave: Leave) => (
            <div key={leave.id} className="rounded-lg border p-4">
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedIds.includes(leave.id)}
                  onChange={() => toggleSelect(leave.id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />

                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{TYPE_LABELS[leave.type]}</span>
                        <span className="text-sm text-muted-foreground">
                          {leave.startDate} ~ {leave.endDate} ({leave.days}天)
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{leave.reason}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => void handleApprove(leave.id)}
                        disabled={approveMutation.isPending}
                        className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        <Check className="h-4 w-4" />
                        批准
                      </button>
                      <button
                        onClick={() => setRejectingId(leave.id)}
                        className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                      >
                        <X className="h-4 w-4" />
                        拒绝
                      </button>
                    </div>
                  </div>

                  {rejectingId === leave.id && (
                    <div className="mt-4 space-y-2 border-t pt-4">
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="请输入拒绝原因"
                        rows={2}
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setRejectingId(null)
                            setRejectReason('')
                          }}
                          className="rounded-md px-3 py-1.5 text-sm hover:bg-muted"
                        >
                          取消
                        </button>
                        <button
                          onClick={() => handleReject(leave.id)}
                          disabled={rejectMutation.isPending}
                          className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          确认拒绝
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog />
    </div>
  )
}
